import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  formatBytes,
  formatDate,
  abreEnEditor,
  urlEditor,
  type PublicLinkEntry,
  type SharedEntry,
} from './api';
import { HistorialDialog } from './Historial';
import { IconoDe } from './Icono';
import { BarraSeleccion, Casilla, CasillaTodos, enLote, resumenLote, useSeleccion } from './seleccion';
import { Confirmar, MenuAcciones } from './ui';

/** Compartido conmigo: permite entrar a las carpetas compartidas. */
export function CompartidoConmigo() {
  const [items, setItems] = useState<SharedEntry[]>([]);
  const [dentroDe, setDentroDe] = useState<{ key: string; nombre: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [historial, setHistorial] = useState<SharedEntry | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setItems(dentroDe ? await api.browseShared(dentroDe.key) : await api.sharesReceived());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar los compartidos.');
    } finally {
      setCargando(false);
    }
  }, [dentroDe]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function descargar(entry: SharedEntry) {
    try {
      const { url } = await api.sharedDownloadUrl(entry.objectKey);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos descargar.');
    }
  }

  return (
    <section>
      {dentroDe && (
        <nav className="breadcrumb">
          <button className="link" onClick={() => setDentroDe(null)}>
            Compartido conmigo
          </button>
          <span className="separador">/</span>
          <span className="actual">{dentroDe.nombre}</span>
        </nav>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {cargando && <p className="vacio">Cargando…</p>}

      {!cargando && items.length === 0 && (
        <p className="vacio">Todavía nadie compartió nada con vos.</p>
      )}

      {!cargando && items.length > 0 && (
        <table className="tabla">
          <thead>
            <tr>
              <th>Nombre</th>
              {!dentroDe && <th>Compartió</th>}
              <th className="col-tamanio">Tamaño</th>
              <th className="col-tamanio">Permiso</th>
              <th className="col-acciones" />
            </tr>
          </thead>
          <tbody>
            {items.map((entry) => (
              <tr key={entry.objectKey}>
                <td>
                  {entry.kind === 'folder' ? (
                    <button
                      className="enlace-carpeta"
                      onClick={() => setDentroDe({ key: entry.objectKey, nombre: entry.name })}
                    >
                      <IconoDe nombre={entry.name} esCarpeta /> {entry.name}
                    </button>
                  ) : (
                    <span>
                      <IconoDe nombre={entry.name} esCarpeta={false} /> {entry.name}
                    </span>
                  )}
                </td>
                {!dentroDe && <td>{entry.counterpart}</td>}
                <td className="col-tamanio">
                  {entry.kind === 'file' ? formatBytes(entry.sizeBytes) : ''}
                </td>
                <td className="col-tamanio">
                  {entry.permission === 'write' ? 'Edición' : 'Lectura'}
                </td>
                <td className="col-acciones">
                  {entry.kind === 'file' && (
                    <MenuAcciones
                      etiqueta={`Acciones de ${entry.name}`}
                      opciones={[
                        {
                          etiqueta: entry.permission === 'write' ? 'Abrir en el editor' : 'Ver en el editor',
                          onSelect: () =>
                            window.open(urlEditor({ key: entry.objectKey }), '_blank', 'noopener'),
                          oculto: !abreEnEditor(entry.name),
                        },
                        {
                          etiqueta: 'Historial de versiones…',
                          onSelect: () => setHistorial(entry),
                          oculto: !abreEnEditor(entry.name),
                        },
                        { etiqueta: 'Descargar', onSelect: () => descargar(entry) },
                      ]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {historial && (
        <HistorialDialog
          ubicacion={{ key: historial.objectKey }}
          nombre={historial.name}
          onClose={() => setHistorial(null)}
        />
      )}
    </section>
  );
}

/** Compartido por mí: permite dejar de compartir. */
export function CompartidoPorMi() {
  const [items, setItems] = useState<SharedEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [porQuitar, setPorQuitar] = useState<SharedEntry[] | null>(null);
  const claves = useMemo(() => items.map((i) => i.id), [items]);
  const sel = useSeleccion(claves);
  const seleccionados = items.filter((i) => sel.seleccion.has(i.id));

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await api.sharesSent());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar la lista.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function dejarDeCompartir(lista: SharedEntry[]) {
    setPorQuitar(null);
    const r = await enLote(lista, (e) => api.unshare(e.id));
    const resumen = resumenLote(r, lista.length, (e) => `${e.name} (${e.counterpart})`, (c) =>
      `Se dejaron de compartir ${c}.`,
    );
    setAviso(lista.length === 1 ? null : resumen.aviso);
    setError(resumen.error);
    sel.limpiar();
    await cargar();
  }

  if (cargando) return <p className="vacio">Cargando…</p>;

  return (
    <section>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {aviso && <p className="aviso-ok">{aviso}</p>}

      <BarraSeleccion cantidad={seleccionados.length} onLimpiar={sel.limpiar}>
        <button className="peligro-solido" onClick={() => setPorQuitar(seleccionados)}>
          Dejar de compartir
        </button>
      </BarraSeleccion>

      {items.length === 0 ? (
        <p className="vacio">No compartiste nada todavía.</p>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th className="col-check">
                <CasillaTodos estado={sel.estado} onChange={sel.alternarTodos} etiqueta="Seleccionar todo" />
              </th>
              <th>Nombre</th>
              <th>Compartido con</th>
              <th className="col-tamanio">Permiso</th>
              <th className="col-fecha">Desde</th>
              <th className="col-acciones" />
            </tr>
          </thead>
          <tbody>
            {items.map((entry) => (
              <tr key={entry.id} className={sel.seleccion.has(entry.id) ? 'seleccionada' : ''}>
                <td className="col-check">
                  <Casilla
                    marcada={sel.seleccion.has(entry.id)}
                    onCambio={(shift) => sel.alternar(entry.id, shift)}
                    etiqueta={`Seleccionar ${entry.name} con ${entry.counterpart}`}
                  />
                </td>
                <td>
                  <IconoDe nombre={entry.name} esCarpeta={entry.kind === 'folder'} /> {entry.name}
                </td>
                <td>{entry.counterpart}</td>
                <td className="col-tamanio">
                  {entry.permission === 'write' ? 'Edición' : 'Lectura'}
                </td>
                <td className="col-fecha">{formatDate(entry.createdAt)}</td>
                <td className="col-acciones">
                  <button className="link peligro" onClick={() => setPorQuitar([entry])}>
                    Dejar de compartir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {porQuitar && (
        <Confirmar
          titulo="Dejar de compartir"
          mensaje={
            porQuitar.length === 1
              ? `${porQuitar[0].counterpart} pierde el acceso a "${porQuitar[0].name}" al instante.`
              : `Se quitan ${porQuitar.length} compartidos. Las personas pierden el acceso al instante.`
          }
          etiquetaAceptar="Dejar de compartir"
          peligro
          onCancelar={() => setPorQuitar(null)}
          onAceptar={() => dejarDeCompartir(porQuitar)}
        />
      )}
    </section>
  );
}

export function LinksPublicos() {
  const [items, setItems] = useState<PublicLinkEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<number | null>(null);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);
  const [porRevocar, setPorRevocar] = useState<PublicLinkEntry[] | null>(null);
  // Solo los activos se pueden seleccionar: desactivar uno inactivo no hace nada.
  const claves = useMemo(() => items.filter((i) => i.active).map((i) => i.id), [items]);
  const sel = useSeleccion(claves);
  const seleccionados = items.filter((i) => sel.seleccion.has(i.id));

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await api.publicLinks());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar los links.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function revocar(lista: PublicLinkEntry[]) {
    setPorRevocar(null);
    const r = await enLote(lista, (e) => api.revokePublicLink(e.id));
    const resumen = resumenLote(r, lista.length, (e) => e.name, (c) => `Se desactivaron ${c} links.`);
    setAviso(lista.length === 1 ? null : resumen.aviso);
    setError(resumen.error);
    sel.limpiar();
    await cargar();
  }

  async function copiar(entry: PublicLinkEntry) {
    await navigator.clipboard.writeText(entry.url);
    setCopiado(entry.id);
    setTimeout(() => setCopiado(null), 1500);
  }

  if (cargando) return <p className="vacio">Cargando…</p>;

  return (
    <section>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {aviso && <p className="aviso-ok">{aviso}</p>}

      <BarraSeleccion cantidad={seleccionados.length} onLimpiar={sel.limpiar}>
        <button className="peligro-solido" onClick={() => setPorRevocar(seleccionados)}>
          Desactivar
        </button>
      </BarraSeleccion>

      {items.length === 0 ? (
        <p className="vacio">
          No creaste links públicos. Se crean desde el explorador, en el menú de acciones del archivo.
        </p>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th className="col-check">
                {claves.length > 0 && (
                  <CasillaTodos estado={sel.estado} onChange={sel.alternarTodos} etiqueta="Seleccionar todos los activos" />
                )}
              </th>
              <th>Archivo</th>
              <th className="col-tamanio">Descargas</th>
              <th className="col-fecha">Vence</th>
              <th className="col-tamanio">Estado</th>
              <th className="col-acciones" />
            </tr>
          </thead>
          <tbody>
            {items.map((entry) => (
              <tr key={entry.id} className={sel.seleccion.has(entry.id) ? 'seleccionada' : entry.active ? '' : 'fila-inactiva'}>
                <td className="col-check">
                  {entry.active && (
                    <Casilla
                      marcada={sel.seleccion.has(entry.id)}
                      onCambio={(shift) => sel.alternar(entry.id, shift)}
                      etiqueta={`Seleccionar el link de ${entry.name}`}
                    />
                  )}
                </td>
                <td>
                  <IconoDe nombre={entry.name} esCarpeta={false} /> {entry.name}
                </td>
                <td className="col-tamanio">
                  {entry.downloadCount} / {entry.downloadLimit ?? '∞'}
                </td>
                <td className="col-fecha">{formatDate(entry.expiresAt)}</td>
                <td className="col-tamanio">
                  <span className={entry.active ? 'chip activo' : 'chip inactivo'}>
                    {entry.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="col-acciones">
                  <button className="link" onClick={() => copiar(entry)}>
                    {copiado === entry.id ? 'Copiado' : 'Copiar link'}
                  </button>
                  {entry.active && (
                    <button className="link peligro" onClick={() => setPorRevocar([entry])}>
                      Desactivar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {porRevocar && (
        <Confirmar
          titulo={porRevocar.length === 1 ? 'Desactivar link' : `Desactivar ${porRevocar.length} links`}
          mensaje={
            porRevocar.length === 1
              ? `El link de "${porRevocar[0].name}" deja de funcionar al instante. Quien lo tenga ya no va a poder descargar.`
              : `Los ${porRevocar.length} links dejan de funcionar al instante. Quien los tenga ya no va a poder descargar.`
          }
          etiquetaAceptar="Desactivar"
          peligro
          onCancelar={() => setPorRevocar(null)}
          onAceptar={() => revocar(porRevocar)}
        />
      )}
    </section>
  );
}
